import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, FileText, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ImageField } from "@/admin/MediaPicker";

const PAGES = [
  { slug: "chi-siamo", label: "Chi siamo", url: "/chi-siamo" },
  { slug: "faq", label: "FAQ", url: "/faq" },
];
const LANGS = ["it", "en", "es"];
const uid = () => Math.random().toString(36).slice(2, 10);

const move = (list, index, dir) => {
  const to = index + dir;
  if (to < 0 || to >= list.length) return list;
  const out = [...list];
  [out[index], out[to]] = [out[to], out[index]];
  return out;
};

export default function AdminPages() {
  const [slug, setSlug] = useState(PAGES[0].slug);
  const [page, setPage] = useState(null);
  const [langTab, setLangTab] = useState("it");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPage(null);
    api.get(`/admin/pages/${slug}`).then((r) => setPage(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [slug]);

  const setF = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setTr = (k, v) =>
    setPage((p) => ({
      ...p,
      translations: { ...(p.translations || {}), [langTab]: { ...((p.translations || {})[langTab] || {}), [k]: v } },
    }));
  const val = (k) => (langTab === "it" ? page[k] || "" : ((page.translations || {})[langTab] || {})[k] || "");
  const setVal = (k, v) => (langTab === "it" ? setF(k, v) : setTr(k, v));

  const setItem = (key, id, patch) =>
    setPage((p) => ({ ...p, [key]: p[key].map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const setItemVal = (key, item, field, v) =>
    langTab === "it"
      ? setItem(key, item.id, { [field]: v })
      : setItem(key, item.id, {
          translations: {
            ...(item.translations || {}),
            [langTab]: { ...((item.translations || {})[langTab] || {}), [field]: v },
          },
        });
  const itemVal = (item, field) =>
    langTab === "it" ? item[field] || "" : ((item.translations || {})[langTab] || {})[field] || "";

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/pages/${slug}`, page);
      setPage(r.data);
      toast.success("Pagina aggiornata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!page) return <p className="text-sand text-sm">Carico…</p>;

  const isFaq = slug === "faq";

  return (
    <div data-testid="admin-pages">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl flex items-center gap-3"><FileText className="h-6 w-6 text-copper" /> Pagine & SEO</h1>
          <p className="text-cream2 text-sm mt-1">Testi delle pagine "Chi siamo" e "FAQ" e i campi che Google mostra nei risultati di ricerca. Le linguette EN/ES sono le traduzioni: se le lasci vuote il sito usa l'italiano.</p>
        </div>
        <button onClick={save} disabled={busy} className="btn-copper !px-5 !py-2.5 disabled:opacity-50" data-testid="pages-save">
          <Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva"}
        </button>
      </div>

      <div className="flex items-center gap-2 mt-8">
        {PAGES.map((p) => (
          <button
            key={p.slug}
            type="button"
            onClick={() => setSlug(p.slug)}
            className={"font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors " + (slug === p.slug ? "border-copper text-copperlight" : "border-white/10 text-sand hover:text-cream")}
            data-testid={`pages-tab-${p.slug}`}
          >
            {p.label}
          </button>
        ))}
        <a href={PAGES.find((p) => p.slug === slug).url} target="_blank" rel="noopener noreferrer" className="ml-auto font-mono text-[11px] uppercase tracking-widest text-sand hover:text-copperlight" data-testid="pages-preview-link">
          Vedi la pagina →
        </a>
      </div>

      <div className="flex items-center gap-2 mt-6">
        {LANGS.map((lg) => (
          <button
            key={lg}
            type="button"
            onClick={() => setLangTab(lg)}
            className={"font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors " + (langTab === lg ? "border-copper text-copperlight" : "border-white/10 text-sand hover:text-cream")}
            data-testid={`pages-lang-tab-${lg}`}
          >
            {lg}
          </button>
        ))}
      </div>

      <h2 className="font-serif text-2xl mt-10 mb-1">Titolo e apertura</h2>
      <p className="text-sand text-sm mb-4">Il titolo grande in cima alla pagina e il paragrafo di apertura.</p>
      <div className="space-y-4">
        <label className="block">
          <span className="label">Titolo della pagina {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
          <input className="field" value={val("title")} onChange={(e) => setVal("title", e.target.value)} data-testid="pages-title" />
        </label>
        <label className="block">
          <span className="label">Paragrafo di apertura {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
          <textarea rows="4" className="field resize-none" value={val("intro")} onChange={(e) => setVal("intro", e.target.value)} data-testid="pages-intro" />
        </label>
        {!isFaq && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ImageField label="Immagine della pagina" value={page.cover} onChange={(v) => setF("cover", v)} testId="pages-cover" />
          </div>
        )}
      </div>

      <h2 className="font-serif text-2xl mt-12 mb-1">Come appare su Google e sui social</h2>
      <p className="text-sand text-sm mb-4">Se lasci vuoti questi campi il sito usa i testi predefiniti. Lunghezze consigliate: titolo fino a 60 caratteri, descrizione fino a 155.</p>
      <div className="space-y-4">
        <label className="block">
          <span className="label">Titolo per i motori di ricerca {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
          <input className="field" value={val("seo_title")} onChange={(e) => setVal("seo_title", e.target.value)} data-testid="pages-seo-title" />
          <span className="font-mono text-[10px] text-sand">{val("seo_title").length} caratteri</span>
        </label>
        <label className="block">
          <span className="label">Descrizione per i motori di ricerca {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
          <textarea rows="3" className="field resize-none" value={val("seo_description")} onChange={(e) => setVal("seo_description", e.target.value)} data-testid="pages-seo-desc" />
          <span className="font-mono text-[10px] text-sand">{val("seo_description").length} caratteri</span>
        </label>
      </div>

      {isFaq ? (
        <>
          <div className="flex items-end justify-between gap-4 mt-12 mb-4 flex-wrap">
            <div>
              <h2 className="font-serif text-2xl mb-1">Domande e risposte</h2>
              <p className="text-sand text-sm">L'ordine è quello della pagina. Le domande nascoste non vengono pubblicate.</p>
            </div>
            <button
              type="button"
              onClick={() => setF("faqs", [...page.faqs, { id: uid(), question: "", answer: "", visible: true, translations: {} }])}
              className="font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 border border-copper/40 text-copperlight flex items-center gap-2"
              data-testid="pages-faq-add"
            >
              <Plus className="h-4 w-4" /> Aggiungi domanda
            </button>
          </div>
          <div className="space-y-4" data-testid="pages-faq-list">
            {page.faqs.map((f, i) => (
              <div key={f.id} className={"card-dark p-5 " + (f.visible ? "" : "opacity-60")} data-testid={`pages-faq-${i + 1}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-mono text-[11px] text-copper tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setF("faqs", move(page.faqs, i, -1))} className="p-2 border border-white/10 text-sand hover:text-cream" data-testid={`pages-faq-up-${i + 1}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setF("faqs", move(page.faqs, i, 1))} className="p-2 border border-white/10 text-sand hover:text-cream" data-testid={`pages-faq-down-${i + 1}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button
                      type="button"
                      onClick={() => setItem("faqs", f.id, { visible: !f.visible })}
                      className={"font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-2 " + (f.visible ? "border-copper text-copperlight" : "border-white/10 text-sand")}
                      data-testid={`pages-faq-visible-${i + 1}`}
                    >
                      {f.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {f.visible ? "Pubblicata" : "Nascosta"}
                    </button>
                    <button type="button" onClick={() => setF("faqs", page.faqs.filter((x) => x.id !== f.id))} className="p-2 border border-red-500/30 text-red-400/90 hover:text-red-300" data-testid={`pages-faq-delete-${i + 1}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <label className="block">
                    <span className="label">Domanda {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
                    <input className="field" value={itemVal(f, "question")} onChange={(e) => setItemVal("faqs", f, "question", e.target.value)} data-testid={`pages-faq-question-${i + 1}`} />
                  </label>
                  <label className="block">
                    <span className="label">Risposta {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
                    <textarea rows="4" className="field resize-none" value={itemVal(f, "answer")} onChange={(e) => setItemVal("faqs", f, "answer", e.target.value)} data-testid={`pages-faq-answer-${i + 1}`} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-end justify-between gap-4 mt-12 mb-4 flex-wrap">
            <div>
              <h2 className="font-serif text-2xl mb-1">Sezioni della pagina</h2>
              <p className="text-sand text-sm">Ogni sezione è un titolo con il suo testo, nell'ordine in cui compare.</p>
            </div>
            <button
              type="button"
              onClick={() => setF("sections", [...page.sections, { id: uid(), title: "", text: "", visible: true, translations: {} }])}
              className="font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 border border-copper/40 text-copperlight flex items-center gap-2"
              data-testid="pages-section-add"
            >
              <Plus className="h-4 w-4" /> Aggiungi sezione
            </button>
          </div>
          <div className="space-y-4" data-testid="pages-section-list">
            {page.sections.map((s, i) => (
              <div key={s.id} className={"card-dark p-5 " + (s.visible ? "" : "opacity-60")} data-testid={`pages-section-${i + 1}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-mono text-[11px] text-copper tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</p>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setF("sections", move(page.sections, i, -1))} className="p-2 border border-white/10 text-sand hover:text-cream" data-testid={`pages-section-up-${i + 1}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setF("sections", move(page.sections, i, 1))} className="p-2 border border-white/10 text-sand hover:text-cream" data-testid={`pages-section-down-${i + 1}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button
                      type="button"
                      onClick={() => setItem("sections", s.id, { visible: !s.visible })}
                      className={"font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border flex items-center gap-2 " + (s.visible ? "border-copper text-copperlight" : "border-white/10 text-sand")}
                      data-testid={`pages-section-visible-${i + 1}`}
                    >
                      {s.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {s.visible ? "Pubblicata" : "Nascosta"}
                    </button>
                    <button type="button" onClick={() => setF("sections", page.sections.filter((x) => x.id !== s.id))} className="p-2 border border-red-500/30 text-red-400/90 hover:text-red-300" data-testid={`pages-section-delete-${i + 1}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <label className="block">
                    <span className="label">Titolo della sezione {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
                    <input className="field" value={itemVal(s, "title")} onChange={(e) => setItemVal("sections", s, "title", e.target.value)} data-testid={`pages-section-title-${i + 1}`} />
                  </label>
                  <label className="block">
                    <span className="label">Testo {langTab !== "it" && `(${langTab.toUpperCase()})`}</span>
                    <textarea rows="6" className="field resize-none" value={itemVal(s, "text")} onChange={(e) => setItemVal("sections", s, "text", e.target.value)} data-testid={`pages-section-text-${i + 1}`} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end mt-8">
        <button onClick={save} disabled={busy} className="btn-copper !px-6 !py-2.5 disabled:opacity-50" data-testid="pages-save-bottom">
          <Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}
